import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import { TrophyIcon } from './icons';
import { useAuthContext } from '../contexts/AuthContext';

interface PublicLayoutProps {
  children: ReactNode;
  hideHeader?: boolean;
}

export default function PublicLayout({ children, hideHeader = false }: PublicLayoutProps) {
  const { isAuthenticated } = useAuthContext();

  return (
    <div className="min-h-screen flex flex-col">
      {!hideHeader && (
        <header className="bg-white shadow-sm">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="text-xl font-bold text-purple-700 flex items-center gap-2">
              <TrophyIcon className="w-5 h-5" />
              Takedown League
            </Link>
            <nav className="flex items-center gap-6">
              <Link
                to="/about"
                className="text-sm text-gray-600 hover:text-purple-700 transition-colors"
              >
                About
              </Link>
              <Link
                to="/how-it-works"
                className="text-sm text-gray-600 hover:text-purple-700 transition-colors"
              >
                How It Works
              </Link>
              <Link
                to="/tournaments"
                className="text-sm text-gray-600 hover:text-purple-700 transition-colors"
              >
                Tournaments
              </Link>
              <Link
                to="/blog"
                className="text-sm text-gray-600 hover:text-purple-700 transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/faq"
                className="text-sm text-gray-600 hover:text-purple-700 transition-colors"
              >
                FAQ
              </Link>
              {isAuthenticated ? (
                <Link
                  to="/leagues"
                  className="text-sm bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Back to App
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="text-sm bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        </header>
      )}

      <main className="flex-1">{children}</main>

      <footer className="bg-gray-900 text-gray-400 py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-sm hover:text-white transition-colors">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="text-sm hover:text-white transition-colors">
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link to="/tournaments" className="text-sm hover:text-white transition-colors">
                    Tournaments
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-sm hover:text-white transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-sm hover:text-white transition-colors">
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              {isAuthenticated ? (
                <>
                  <h4 className="text-white font-semibold mb-3 text-sm">My Account</h4>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/leagues" className="text-sm hover:text-white transition-colors">
                        My Leagues
                      </Link>
                    </li>
                    <li>
                      <Link to="/profile" className="text-sm hover:text-white transition-colors">
                        My Profile
                      </Link>
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  <h4 className="text-white font-semibold mb-3 text-sm">Get Started</h4>
                  <ul className="space-y-2">
                    <li>
                      <Link
                        to="/login?tab=register"
                        className="text-sm hover:text-white transition-colors"
                      >
                        Create Account
                      </Link>
                    </li>
                    <li>
                      <Link to="/login" className="text-sm hover:text-white transition-colors">
                        Sign In
                      </Link>
                    </li>
                  </ul>
                </>
              )}
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-sm hover:text-white transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <a
                    href="https://takedownleague.tawk.help/category/getting-started"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Help Center
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Takedown League. All rights reserved.
            </p>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-sm hover:text-white transition-colors">
                Terms
              </Link>
              <a
                href="https://takedownleague.tawk.help/category/getting-started"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:text-white transition-colors"
              >
                Help
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
